<main class="hold-card">
  <!-- HEADER BAND -->
  <div class="hold-head">
    <div class="hold-badge">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round" class="size-[31px]" aria-hidden="true">
        <rect width="20" height="14" x="2" y="5" rx="2" />
        <line x1="2" x2="22" y1="10" y2="10" />
      </svg>
    </div>
    <span class="hold-eyebrow">
      <span class="pulse" aria-hidden="true"></span>Subscription on hold
    </span>
    <h1 class="hold-title use-grotesque">Let's get you back up and running, {{ firstName() }}.</h1>
  </div>

  <!-- BODY -->
  <div class="p-8">
    <p class="mb-6 font-sans text-[16px] font-medium leading-[1.5] text-[var(--foreground)]">
      We couldn't process the payment for your <b class="font-bold">{{ planName() }}</b> plan, so your
      subscription is paused for now. Settle the balance below and everything switches straight back on —
      nothing is lost.
    </p>

    <!-- INVOICE DETAIL -->
    <div class="mb-6 rounded-lg border border-[var(--border)] bg-[var(--background)] px-5 py-2">
      <div class="flex items-center justify-between py-[13px]">
        <span class="inv-key">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
            <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
            <path d="M12 17.5v-11" />
          </svg>
          Amount due
        </span>
        <span class="flex items-center font-sans">
          <span class="use-grotesque text-[19px] font-extrabold text-[var(--brand-purple)] tabular-nums">{{ amountDue()
            }}</span>
          <span
            class="ml-2 rounded-full bg-[#DC2626]/10 px-2 py-[3px] text-[10.5px] font-extrabold uppercase tracking-[0.05em] text-[#DC2626]">{{
            daysOverdue() }} days overdue</span>
        </span>
      </div>
      <div class="flex items-center justify-between border-t border-[var(--border)] py-[13px]">
        <span class="inv-key">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M16 14v2.2l1.6 1" />
            <path d="M16 2v4" />
            <path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5" />
            <path d="M3 10h5" />
            <path d="M8 2v4" />
            <circle cx="16" cy="16" r="6" />
          </svg>
          Due since
        </span>
        <span class="font-sans text-[14px] font-bold text-[var(--foreground)]">{{ dueSince() }}</span>
      </div>
      @if (paymentMethodLabel()) {
      <div class="flex items-center justify-between border-t border-[var(--border)] py-[13px]">
        <span class="inv-key">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect width="20" height="14" x="2" y="5" rx="2" />
            <line x1="2" x2="22" y1="10" y2="10" />
          </svg>
          Payment method
        </span>
        <span class="font-sans text-[14px] font-bold text-[var(--foreground)]">{{ paymentMethodLabel() }}</span>
      </div>
      }
    </div>

    <!-- REASSURANCE -->
    <div
      class="mb-6 flex items-start gap-[11px] rounded-lg border border-[#0B8F4E]/[0.18] bg-[#0B8F4E]/[0.07] px-4 py-[14px]">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round" class="mt-px size-[19px] shrink-0 text-[#0B8F4E]"
        aria-hidden="true">
        <path
          d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
      <p class="m-0 font-sans text-[13.5px] font-medium leading-[1.45] text-[var(--foreground)]">
        <b class="font-bold">Your data is safe.</b> Briefs, creators and content stay exactly as you left them.
        You have until <b class="font-bold">{{ gracePeriodEndsAt() }}</b> to resolve this before active
        campaigns are archived.
      </p>
    </div>

    <!-- ACTIONS -->
    <div class="flex gap-3 flex-row items-center justify-between">

      <button type="button" *appHasRole="[4,6]" (click)="openJumpInModal()"
        class="shrink-0 inline-flex items-center justify-center rounded-md secondary-button text-primary">
        Jump In Anyway
      </button>

      <button type="button" class="flex-1 hold-btn hold-btn-primary" [disabled]="primaryBusy()"
        (click)="onPrimaryCta()">
        @if (primaryBusy()) {
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round" class="hold-spin" aria-hidden="true">
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
        Please wait…
        } @else {
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect width="20" height="14" x="2" y="5" rx="2" />
          <line x1="2" x2="22" y1="10" y2="10" />
        </svg>
        {{ primaryCtaLabel() }}
        }
      </button>
    </div>

    <div class="mt-3 flex items-center justify-center gap-4 text-center">
      @if (hasOutstanding()) {
      <button type="button"
        class="font-sans text-[13px] font-semibold text-[var(--brand-purple)] underline disabled:opacity-50"
        [disabled]="openingPortal()" (click)="updatePaymentMethod()">
        Or update the card on file
      </button>
      }
      <button type="button"
        class="font-sans text-[13px] font-semibold text-[var(--muted-foreground)] underline disabled:opacity-50"
        [disabled]="retrying()" (click)="retryPayment()">
        @if (retrying()) { Checking… } @else { I've paid — refresh status }
      </button>
    </div>

    @if (actionError()) {
    <p class="mt-3 font-sans text-[13px] font-semibold text-[#DC2626]" role="alert">{{ actionError() }}</p>
    }

    @if (payError()) {
    <p class="mt-3 font-sans text-[13px] font-semibold text-[#DC2626]" role="alert">{{ payError() }}</p>
    }

    @if (payNotice()) {
    <p class="mt-3 font-sans text-[13px] font-semibold text-[#0B8F4E]" role="status">{{ payNotice() }}</p>
    }

    <!-- FOOT -->
    <div
      class="mt-6 flex items-center justify-between gap-3 border-t border-[var(--border)] pt-5 font-sans text-[13px] font-medium text-[var(--muted-foreground)]">
      <button type="button" class="btn-shout btn-shout-sm btn-shout-tertiary shrink-0" [disabled]="loggingOut()"
        (click)="logout()">
        {{ loggingOut() ? 'Logging out…' : 'Log out' }}
      </button>
      <a class="inline-flex items-center gap-[5px] font-bold text-[var(--brand-purple)]"
        [href]="'mailto:' + supportEmail">
        Contact billing support
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round" class="size-[14px]" aria-hidden="true">
          <path d="M5 12h14" />
          <path d="m12 5 7 7-7 7" />
        </svg>
      </a>
    </div>


  </div>
</main>

@if (showJumpInModal()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
  <div
    class="w-full max-w-sm rounded-xl bg-white dark:bg-zinc-900 p-6 shadow-xl ring-1 ring-gray-200 dark:ring-zinc-700"
    role="dialog" aria-modal="true" aria-labelledby="jump-in-title">
    <h3 id="jump-in-title" class="text-base font-semibold text-primary">Jump in anyway</h3>
    <p class="mt-1 text-sm text-secondary">Enter your API key to bypass the subscription hold for this session.</p>

    <div class="mt-4">
      <label for="jump-in-api-key" class="block text-sm font-medium text-primary">API key</label>
      <input id="jump-in-api-key" type="password" [value]="apiKeyInput()"
        (input)="apiKeyInput.set($any($event.target).value)"
        class="mt-1.5 block w-full rounded-md bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-primary outline-1 -outline-offset-1 outline-gray-300 dark:outline-zinc-600 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary"
        placeholder="Enter API key" autocomplete="current-password" />
    </div>

    @if (jumpInError()) {
    <p class="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">{{ jumpInError() }}</p>
    }

    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelJumpIn()" [disabled]="jumpInLoading()"
        class="rounded-md px-3.5 py-2 text-sm font-semibold text-primary ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-800 disabled:opacity-50">
        Cancel
      </button>
      <button type="button" (click)="confirmJumpIn()" [disabled]="jumpInLoading() || !apiKeyInput()"
        class="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50">
        @if (jumpInLoading()) {
        Jumping in…
        } @else {
        Confirm
        }
      </button>
    </div>
  </div>
</div>
}
